/**
 * Lightweight mowing plan model used by the live preview on
 * /tools/mowing-calculator. Drives the deck-height + frequency
 * readout, the ruler diagram, and the 12-month mowing-intensity
 * heat strip.
 *
 * The full engine (`calculateMowingRecommendations`) still runs at
 * submit time for the post-submit detailed view.
 */

export type MowGrassId =
  | "bermuda"
  | "zoysia"
  | "staug"
  | "centipede"
  | "bahia"
  | "fescue"
  | "kbg"
  | "rye"
  | "kikuyu"
  | "annualrye"
  | "coolmix"

export type MowSeasonId = "spring" | "summer" | "fall" | "winter"
export type MowerTypeId = "rotary" | "reel" | "robotic"

export interface MowGrassDef {
  id: MowGrassId
  name: string
  /** Canonical key in data/grass-characteristics.ts. May differ from `name`, which is the compact card label. */
  engineKey: string
  season: "warm" | "cool"
  tag: string
  /** Inches by season. */
  height: Record<MowSeasonId, number>
  /** Frequency in days between mows by season. */
  freq: Record<MowSeasonId, number>
  growth: "slow" | "medium" | "fast"
  minH: number
  maxH: number
}

export const M_GRASSES: Record<MowGrassId, MowGrassDef> = {
  bermuda: {
    id: "bermuda",
    name: "Bermudagrass",
    engineKey: "Bermudagrass",
    season: "warm",
    tag: "Warm-season",
    height: { spring: 1.5, summer: 1.5, fall: 2.0, winter: 2.0 },
    freq: { spring: 5, summer: 4, fall: 7, winter: 14 },
    growth: "fast",
    minH: 0.5,
    maxH: 2.5,
  },
  zoysia: {
    id: "zoysia",
    name: "Zoysia",
    engineKey: "Zoysiagrass",
    season: "warm",
    tag: "Warm-season",
    height: { spring: 1.5, summer: 2.0, fall: 2.0, winter: 2.0 },
    freq: { spring: 7, summer: 5, fall: 10, winter: 21 },
    growth: "medium",
    minH: 0.75,
    maxH: 2.5,
  },
  staug: {
    id: "staug",
    name: "St. Augustine",
    engineKey: "St. Augustinegrass",
    season: "warm",
    tag: "Warm-season",
    height: { spring: 3.5, summer: 4.0, fall: 3.5, winter: 3.5 },
    freq: { spring: 7, summer: 5, fall: 10, winter: 21 },
    growth: "fast",
    minH: 2.5,
    maxH: 4.5,
  },
  centipede: {
    id: "centipede",
    name: "Centipede",
    engineKey: "Centipede",
    season: "warm",
    tag: "Warm-season",
    height: { spring: 1.5, summer: 2.0, fall: 2.0, winter: 2.0 },
    freq: { spring: 10, summer: 7, fall: 14, winter: 28 },
    growth: "slow",
    minH: 1.0,
    maxH: 2.5,
  },
  bahia: {
    id: "bahia",
    name: "Bahiagrass",
    engineKey: "Bahiagrass",
    season: "warm",
    tag: "Warm-season",
    height: { spring: 3.0, summer: 3.5, fall: 3.0, winter: 3.0 },
    freq: { spring: 7, summer: 5, fall: 10, winter: 21 },
    growth: "medium",
    minH: 2.0,
    maxH: 4.0,
  },
  fescue: {
    id: "fescue",
    name: "Tall fescue",
    engineKey: "Tall Fescue",
    season: "cool",
    tag: "Cool-season",
    height: { spring: 3.5, summer: 4.0, fall: 3.5, winter: 3.0 },
    freq: { spring: 5, summer: 7, fall: 5, winter: 14 },
    growth: "fast",
    minH: 2.5,
    maxH: 4.5,
  },
  kbg: {
    id: "kbg",
    name: "Kentucky bluegrass",
    engineKey: "Kentucky Bluegrass",
    season: "cool",
    tag: "Cool-season",
    height: { spring: 2.5, summer: 3.5, fall: 2.5, winter: 2.5 },
    freq: { spring: 5, summer: 7, fall: 5, winter: 14 },
    growth: "medium",
    minH: 1.5,
    maxH: 4.0,
  },
  rye: {
    id: "rye",
    name: "Ryegrass",
    engineKey: "Perennial Ryegrass",
    season: "cool",
    tag: "Cool-season",
    height: { spring: 2.0, summer: 2.5, fall: 2.0, winter: 2.0 },
    freq: { spring: 5, summer: 7, fall: 5, winter: 14 },
    growth: "fast",
    minH: 1.5,
    maxH: 3.0,
  },
  kikuyu: {
    id: "kikuyu",
    name: "Kikuyugrass",
    engineKey: "Kikuyugrass",
    season: "warm",
    tag: "Warm-season",
    // Mow low and often — kikuyu gets stemmy and scalps badly if left tall.
    height: { spring: 1.5, summer: 1.5, fall: 1.5, winter: 2.0 },
    freq: { spring: 5, summer: 4, fall: 7, winter: 14 },
    growth: "fast",
    minH: 0.75,
    maxH: 2.5,
  },
  annualrye: {
    id: "annualrye",
    name: "Annual ryegrass",
    engineKey: "Annual Ryegrass",
    season: "cool",
    tag: "Cool-season",
    // Winter overseed annual — most active fall through spring; dies out in southern summer heat.
    height: { spring: 2.0, summer: 2.5, fall: 2.0, winter: 2.0 },
    freq: { spring: 5, summer: 14, fall: 5, winter: 7 },
    growth: "fast",
    minH: 1.0,
    maxH: 3.0,
  },
  coolmix: {
    id: "coolmix",
    name: "Cool-season mix",
    engineKey: "Mixed Cool-Season Blend",
    season: "cool",
    tag: "Cool-season",
    // KBG/rye/fescue blend — 3 inches is the safe middle ground for all components.
    height: { spring: 3.0, summer: 3.5, fall: 3.0, winter: 2.5 },
    freq: { spring: 5, summer: 7, fall: 5, winter: 14 },
    growth: "medium",
    minH: 2.0,
    maxH: 4.0,
  },
}

export const MOW_GRASS_NAME_TO_ID: Record<string, MowGrassId> = {
  Bermudagrass: "bermuda",
  Bermuda: "bermuda",
  Zoysiagrass: "zoysia",
  Zoysia: "zoysia",
  "St. Augustinegrass": "staug",
  "St. Augustine": "staug",
  Centipede: "centipede",
  Bahiagrass: "bahia",
  "Tall Fescue": "fescue",
  "Tall fescue": "fescue",
  "Kentucky Bluegrass": "kbg",
  "Kentucky bluegrass": "kbg",
  "Perennial Ryegrass": "rye",
  Ryegrass: "rye",
  Kikuyugrass: "kikuyu",
  Kikuyu: "kikuyu",
  "Annual Ryegrass": "annualrye",
  "Annual ryegrass": "annualrye",
  "Mixed Cool-Season Blend": "coolmix",
  "Cool-season mix": "coolmix",
}

// Grass names reach the mowing tool as display names ("Kentucky Bluegrass"),
// as URL slugs from deep links and guides ("kentucky-bluegrass",
// "st.-augustinegrass"), or from saved cross-tool inputs. Match on a case- and
// separator-insensitive key so a slug resolves instead of silently falling
// through to the regional default grass.
function normalizeMowGrassKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[-_.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

const MOW_GRASS_NORMALIZED_TO_ID: Record<string, MowGrassId> = Object.fromEntries(
  Object.entries(MOW_GRASS_NAME_TO_ID).map(([name, id]) => [normalizeMowGrassKey(name), id]),
)

export function resolveMowGrassId(name: string | null | undefined): MowGrassId | undefined {
  if (!name) return undefined
  return MOW_GRASS_NAME_TO_ID[name] ?? MOW_GRASS_NORMALIZED_TO_ID[normalizeMowGrassKey(name)]
}

export interface MowerDef {
  id: MowerTypeId
  name: string
  blurb: string
  /** Inches to subtract from baseline grass height (reel/robotic cut a touch lower). */
  notch: number
}

export const M_MOWERS: Record<MowerTypeId, MowerDef> = {
  rotary: { id: "rotary", name: "Rotary", blurb: "Standard push or riding mower.", notch: 0 },
  reel: { id: "reel", name: "Reel", blurb: "Cylinder mower. Lower cuts.", notch: -0.25 },
  robotic: { id: "robotic", name: "Robotic", blurb: "Cuts daily. Mulch a little.", notch: -0.25 },
}

export const M_SEASONS: Array<{ id: MowSeasonId; label: string }> = [
  { id: "spring", label: "Spring" },
  { id: "summer", label: "Summer" },
  { id: "fall", label: "Fall" },
  { id: "winter", label: "Winter" },
]

export const M_MONTH_LABEL = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export interface MowMonthCell {
  i: number
  label: string
  freq: number
  active: boolean
}

export interface MowingPlan {
  grass: MowGrassDef
  mower: MowerDef
  season: MowSeasonId
  /** Ideal cut-to height in inches (after applying mower notch). */
  idealH: number
  /** Mow when the grass reaches this height (1.5× ideal — the 1/3 rule). */
  oneThirdMax: number
  /** Days between mows in the chosen season. */
  freqDays: number
  growingMonths: MowMonthCell[]
}

interface BuildPlanInput {
  grass: MowGrassId
  season: MowSeasonId
  mower: MowerTypeId
}

export function buildMowingPlan({
  grass,
  season,
  mower,
}: BuildPlanInput): MowingPlan {
  const g = M_GRASSES[grass] ?? M_GRASSES.bermuda
  const m = M_MOWERS[mower] ?? M_MOWERS.rotary
  const baseH = g.height[season]
  const idealH = Math.max(g.minH, baseH + m.notch)
  const oneThirdMax = idealH * 1.5
  const freqDays = g.freq[season]

  const growingMonths: MowMonthCell[] = M_MONTH_LABEL.map((label, i) => {
    let s: MowSeasonId
    if (i <= 1 || i === 11) s = "winter"
    else if (i <= 4) s = "spring"
    else if (i <= 7) s = "summer"
    else s = "fall"
    const f = g.freq[s]
    return { i, label, freq: f, active: f <= 14 }
  })

  return { grass: g, mower: m, season, idealH, oneThirdMax, freqDays, growingMonths }
}
